import { useEffect, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Turnstile } from '@marsidev/react-turnstile';
import { PillButton } from '@ab-platform/ui/pill-button';
import { FloatingLabelInput } from '@ab-platform/ui/floating-label-input';
import { SelectField } from '@ab-platform/ui/select-field';
import { DateField } from '@ab-platform/ui/date-field';
import { CountryPicker } from '@ab-platform/ui/country-picker';
import { MunicipalityPicker } from '@ab-platform/ui/municipality-picker';
import { FocusedPageShell } from '@ab-platform/ui/focused-page-shell';
import { FieldError } from '@ab-platform/ui/field-error';
import { Banner } from '@ab-platform/ui/banner';
import {
  STEP_FIELDS,
  type RegistrationFormValues,
} from '@/hooks/useRegistrationForm';
import { useMunicipalitySearch } from '@/hooks/useMunicipalitySearch';
import { PhoneFieldRow } from '@/components/PhoneFieldRow';
import { env, resumableAreaEnabled } from '@/lib/env';
import { precheckIdentity } from '@/lib/api';
import { useWizardSessionIdContext } from '@/hooks/WizardSessionIdContext';
import {
  useRuiLookup,
  ruiLookupMessage,
  ruiLookupTone,
  ruiLookupKey,
  isRuiLookupStale,
  formatDateIT,
} from '@/hooks/useRuiLookup';
import {
  deriveRuiPrefix,
  normalizeRuiDigits,
  isValidRuiDigits,
  composeCanonicalRui,
} from '@/lib/ruiInput';

const SEX_OPTIONS = ['M', 'F'] as const;

export function AdminPage(): JSX.Element {
  const navigate = useNavigate();
  const wizardSessionId = useWizardSessionIdContext();
  const form = useFormContext<RegistrationFormValues>();
  const { register, control, formState, trigger, setValue, getValues } = form;
  const errors = formState.errors.adminUser;

  // ── Identity precheck (PR-F) ───────────────────────────────────────────
  // Inline dry-run del gate di submit. Stati:
  //   idle       → Continua ✓ valido → fa la chiamata
  //   loading    → spinner sul bottone
  //   conflict   → banner "richiesta già a sistema", bottone disabled
  //                finché l'utente non modifica un identificatore
  //   error      → soft warning, l'utente PUÒ proseguire (submit blocca
  //                comunque se duplicato) — anti-enumeration property
  //                preservata
  type PrecheckUiState = 'idle' | 'loading' | 'conflict' | 'error';
  const [precheckState, setPrecheckState] = useState<PrecheckUiState>('idle');
  const [precheckErrorMessage, setPrecheckErrorMessage] = useState<string | null>(
    null,
  );
  const [precheckTurnstileToken, setPrecheckTurnstileToken] =
    useState<string>('');
  // Turnstile token è single-use: ad ogni nuovo tentativo serve un nuovo
  // widget. Bumping `nonce` smonta+rimonta il widget per generare token
  // fresco.
  const [precheckTurnstileNonce, setPrecheckTurnstileNonce] = useState<number>(
    0,
  );

  // Watch `birthCountry` to drive the IT-only MunicipalityPicker. Non-IT
  // births get a free-text `placeOfBirth` input and the cadastral code
  // is cleared so the BE cross-field "IT requires cadastral" rule does
  // not reject the submission.
  const birthCountry = useWatch({
    control,
    name: 'adminUser.birthCountry',
  });
  const isItalianBirth = birthCountry === 'IT';

  // ── PERSON identity sync (PR-D) ─────────────────────────────────────
  // For PF, the admin IS the same person as the org. Sync `fullName` and
  // `fiscalCode` from the organization fields so the BE coupling rule
  // (`org.legalName == admin.fullName` AND `org.fiscalCode ==
  // admin.fiscalCode`) is satisfied without the user typing them twice.
  // The fields render read-only on PERSON branch — to change them the
  // user has to go back to the Organizzazione step.
  const legalNature = useWatch({ control, name: 'organization.legalNature' });
  const orgLegalName = useWatch({ control, name: 'organization.legalName' });
  const orgFiscalCode = useWatch({ control, name: 'organization.fiscalCode' });
  const isPerson = legalNature === 'PERSON';
  // Personal RUI of the responsible — collected only for AGENZIA/BROKER COMPANY
  // (PR-ONBOARDING-COMPANY-RESPONSIBLE-RUI-1). Section is derived by the BE.
  const orgType = useWatch({ control, name: 'organization.orgType' });
  const showResponsibleRui =
    !isPerson && (orgType === 'AGENZIA' || orgType === 'BROKER');

  // PR-ONBOARDING-RUI-AUTO-LOOKUP-1 — on-blur lookup of the responsible RUI +
  // link to the org; auto-fills the (read-only) registration date. The form
  // holds only the 9 numeric digits (UX-INPUT-1); the canonical RUI is composed
  // at lookup/submit egress.
  const responsibleRuiLookup = useRuiLookup();
  const respRuiDate = useWatch({ control, name: 'adminUser.ruiRegistrationDate' });
  // Watch the fields the responsible lookup depends on so the badge invalidates
  // the instant any of them changes (STALE-FIX). orgType/legalNature/org RUI
  // come from the previous step (read here, not edited on this page).
  const adminFullName = useWatch({ control, name: 'adminUser.fullName' });
  const respRuiDigits = useWatch({ control, name: 'adminUser.ruiNumber' }) ?? '';
  const orgRuiDigits = useWatch({ control, name: 'organization.ruiNumber' }) ?? '';
  const respRuiSection = deriveRuiPrefix(orgType);
  // Show the "9 cifre" error only after blur / Continua (UX-INPUT).
  const [respRuiTouched, setRespRuiTouched] = useState<boolean>(false);
  const handleRespRuiBlur = async (): Promise<void> => {
    const digits = normalizeRuiDigits(getValues('adminUser.ruiNumber'));
    if (!isValidRuiDigits(digits)) {
      responsibleRuiLookup.reset();
      return;
    }
    const currentOrgType = getValues('organization.orgType');
    const r = await responsibleRuiLookup.run({
      subject: 'RESPONSIBLE',
      orgType: currentOrgType,
      legalNature: getValues('organization.legalNature'),
      ruiNumber: composeCanonicalRui(currentOrgType, digits),
      declaredName: getValues('adminUser.fullName'),
      organizationRuiNumber: composeCanonicalRui(
        currentOrgType,
        getValues('organization.ruiNumber'),
      ),
      wizardSessionId: wizardSessionId ?? undefined,
    });
    if (r?.found && r.registrationDate) {
      setValue('adminUser.ruiRegistrationDate', r.registrationDate, {
        shouldDirty: true,
        shouldValidate: false,
      });
    }
  };
  // Validity key built from the SAME canonical RUIs used at lookup time.
  const respRuiCurrentKey = ruiLookupKey({
    subject: 'RESPONSIBLE',
    orgType,
    legalNature,
    ruiNumber: composeCanonicalRui(orgType, respRuiDigits),
    declaredName: adminFullName,
    organizationRuiNumber: composeCanonicalRui(orgType, orgRuiDigits),
  });
  const respRuiStale = isRuiLookupStale(
    responsibleRuiLookup.state,
    respRuiCurrentKey,
  );
  // Stale → tone/message null → badge hidden while editing (UX-INPUT-1).
  const respRuiTone = ruiLookupTone(responsibleRuiLookup.state, respRuiStale);
  const respRuiMessage = ruiLookupMessage(
    responsibleRuiLookup.state,
    respRuiStale,
  );

  useEffect(() => {
    if (!isPerson) return;
    setValue('adminUser.fullName', orgLegalName ?? '', {
      shouldValidate: false,
      shouldDirty: false,
    });
  }, [isPerson, orgLegalName, setValue]);

  useEffect(() => {
    if (!isPerson) return;
    setValue(
      'adminUser.fiscalCode',
      (orgFiscalCode ?? '').toUpperCase(),
      { shouldValidate: false, shouldDirty: false },
    );
  }, [isPerson, orgFiscalCode, setValue]);

  // L'utente può "sbloccare" il banner cambiando un identificatore. Tutti
  // i campi rilevanti sono `register`-ed con setValueAs / RHF; un cambio
  // resetta lo stato a `idle` così il prossimo Continua re-prova il gate.
  const adminFiscalCode = useWatch({ control, name: 'adminUser.fiscalCode' });
  const adminEmail = useWatch({ control, name: 'adminUser.email' });
  const orgVatNumber = useWatch({ control, name: 'organization.vatNumber' });
  useEffect(() => {
    if (precheckState === 'conflict' || precheckState === 'error') {
      setPrecheckState('idle');
      setPrecheckErrorMessage(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: reset on identity change only, not on `precheckState` flips
  }, [adminFiscalCode, adminEmail, orgFiscalCode, orgVatNumber]);

  const handleContinue = async (): Promise<void> => {
    // Surface the RUI error on Continua even if the field was never blurred.
    if (showResponsibleRui) setRespRuiTouched(true);
    // Never advance carrying a stale responsible "verificato": refresh first if
    // the inputs changed since the last lookup (STALE-FIX). BE gates at submit.
    if (showResponsibleRui && isValidRuiDigits(respRuiDigits) && respRuiStale) {
      await handleRespRuiBlur();
    }
    const ok = await trigger(
      STEP_FIELDS.admin as unknown as Parameters<typeof trigger>[0],
    );
    if (!ok) return;

    // No Turnstile token yet — disable the network call but allow the
    // user to retry once Cloudflare resolves. Most challenges resolve
    // automatically within ~1s; user clicks Continua a second time.
    if (!precheckTurnstileToken) {
      setPrecheckState('error');
      setPrecheckErrorMessage(
        'Verifica anti-abuso non ancora pronta. Aspetta qualche secondo e riprova.',
      );
      return;
    }

    setPrecheckState('loading');
    setPrecheckErrorMessage(null);
    const values = getValues();
    const result = await precheckIdentity({
      organization: {
        legalNature: values.organization.legalNature,
        fiscalCode: values.organization.fiscalCode || undefined,
        vatNumber: values.organization.vatNumber || undefined,
      },
      adminUser: {
        fiscalCode: values.adminUser.fiscalCode,
        email: values.adminUser.email,
      },
      turnstileToken: precheckTurnstileToken,
      wizardSessionId: wizardSessionId || undefined,
    });
    // Single-use token consumed — force a fresh widget regardless of outcome.
    setPrecheckTurnstileToken('');
    setPrecheckTurnstileNonce((n) => n + 1);

    if (result.kind === 'clean') {
      setPrecheckState('idle');
      // PR-ONB-RESUME-1B — with the resumable area on, the account step
      // (Firebase signup + bind) sits between Referente and Fiscale.
      navigate(
        resumableAreaEnabled
          ? '/registrazione-intermediario/account'
          : '/registrazione-intermediario/fiscale',
      );
      return;
    }
    if (result.kind === 'conflict') {
      setPrecheckState('conflict');
      return;
    }
    // result.kind === 'error' — soft fallback. NEVER block the user on a
    // precheck infrastructure error: the submit-time gate is the source
    // of truth. Surface a hint, allow Continua to proceed on next click.
    setPrecheckState('error');
    setPrecheckErrorMessage(
      result.code === 'TURNSTILE_INVALID'
        ? 'Verifica anti-abuso non riuscita. Riprova.'
        : result.code === 'RATE_LIMITED'
          ? 'Troppe richieste in poco tempo. Aspetta un momento e riprova.'
          : 'Non è stato possibile verificare ora. Puoi proseguire — il controllo finale verrà fatto al submit.',
    );
  };

  return (
    <FocusedPageShell
      title={
        isPerson
          ? 'Conferma i tuoi dati di accesso'
          : 'Chi gestirà l\'area riservata?'
      }
      description={
        isPerson
          ? 'Per la persona fisica i dati di accesso coincidono con quelli dell\'organizzazione. Inserisci email e dati di nascita; per modificare nome o codice fiscale torna alla pagina precedente.'
          : 'L\'amministratore è la persona che, una volta approvata la richiesta, riceverà l\'accesso all\'area dedicata all\'organizzazione. Inserisci i dati del firmatario.'
      }
      onBack={() => navigate('/registrazione-intermediario/indirizzi')}
      footer={
        <PillButton
          type="button"
          size="lg"
          onClick={handleContinue}
          disabled={
            precheckState === 'loading' || precheckState === 'conflict'
          }
          className="w-full"
        >
          {precheckState === 'loading' ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : null}
          {precheckState === 'loading' ? 'Verifica…' : 'Continua'}
        </PillButton>
      }
    >
      {isPerson ? (
        <Banner tone="info" title="Dati derivati dall'organizzazione">
          Nome e codice fiscale sono ripresi dalla pagina precedente. Per
          modificarli torna a "Organizzazione".
        </Banner>
      ) : null}

      {(() => {
        // Re-verify the responsible RUI on full-name blur: nameMatch depends on
        // it, so a changed name must re-run (or stay invalidated) — STALE-FIX.
        const fullNameReg = register('adminUser.fullName');
        return (
          <FloatingLabelInput
            label="Nome e cognome"
            autoComplete="name"
            readOnly={isPerson}
            error={errors?.fullName?.message}
            {...fullNameReg}
            onBlur={(e) => {
              void fullNameReg.onBlur(e);
              if (
                showResponsibleRui &&
                (getValues('adminUser.ruiNumber') ?? '').trim() !== ''
              ) {
                void handleRespRuiBlur();
              }
            }}
          />
        );
      })()}
      <FloatingLabelInput
        label="Email"
        type="email"
        autoComplete="email"
        error={errors?.email?.message}
        {...register('adminUser.email')}
      />
      <FloatingLabelInput
        label="Codice fiscale"
        autoComplete="off"
        readOnly={isPerson}
        error={errors?.fiscalCode?.message}
        // Codice fiscale italiano = sempre uppercase. La regex BE
        // `/^[A-Z0-9]{11,16}$/` rifiuta lowercase, quindi senza
        // normalizzare l'utente digiterebbe in modo naturale e
        // "Continua" non avanzerebbe.
        className="[&_input]:uppercase"
        {...register('adminUser.fiscalCode', {
          setValueAs: (v: unknown) =>
            typeof v === 'string' ? v.toUpperCase() : v,
        })}
      />

      <div className="grid gap-5 sm:grid-cols-2">
        <div className="grid gap-1.5">
          <Controller
            control={control}
            name="adminUser.sex"
            render={({ field }) => (
              <SelectField
                label="Sesso"
                value={field.value ?? ''}
                options={SEX_OPTIONS}
                onChange={field.onChange}
                placeholder="—"
              />
            )}
          />
          <FieldError message={errors?.sex?.message} />
        </div>
        <div className="grid gap-1.5">
          <Controller
            control={control}
            name="adminUser.dateOfBirth"
            render={({ field }) => (
              <DateField
                label="Data di nascita"
                value={field.value ?? ''}
                onChange={field.onChange}
              />
            )}
          />
          <FieldError message={errors?.dateOfBirth?.message} />
        </div>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div className="grid gap-1.5">
          <Controller
            control={control}
            name="adminUser.birthCountry"
            render={({ field }) => (
              <CountryPicker
                label="Paese di nascita"
                value={field.value ?? ''}
                onChange={(iso2) => {
                  field.onChange(iso2);
                  // Switching away from IT clears the cadastral code so
                  // the BE cross-field validation passes; switching back
                  // requires the user to pick a comune again from the
                  // MunicipalityPicker (cadastral codes are IT-only).
                  if (iso2 !== 'IT') {
                    setValue(
                      'adminUser.birthMunicipalityCadastralCode',
                      '',
                      { shouldDirty: true, shouldValidate: false },
                    );
                  }
                }}
              />
            )}
          />
          <FieldError message={errors?.birthCountry?.message} />
        </div>

        <div className="grid gap-1.5">
          {isItalianBirth ? (
            <Controller
              control={control}
              name="adminUser.placeOfBirth"
              render={({ field: placeField }) => (
                <Controller
                  control={control}
                  name="adminUser.birthMunicipalityCadastralCode"
                  render={({ field: codeField }) => (
                    <MunicipalityPicker
                      label="Comune di nascita"
                      value={{
                        name: placeField.value ?? '',
                        cadastralCode: codeField.value ?? null,
                        provinceCode: null,
                      }}
                      onChange={(next) => {
                        placeField.onChange(next.name);
                        codeField.onChange(next.cadastralCode ?? '');
                      }}
                      useSearch={useMunicipalitySearch}
                    />
                  )}
                />
              )}
            />
          ) : (
            <FloatingLabelInput
              label="Luogo di nascita"
              autoComplete="off"
              labelPosition="top"
              error={errors?.placeOfBirth?.message}
              {...register('adminUser.placeOfBirth')}
            />
          )}
          <FieldError
            message={
              isItalianBirth
                ? errors?.birthMunicipalityCadastralCode?.message
                : errors?.placeOfBirth?.message
            }
          />
        </div>
      </div>

      <PhoneFieldRow
        path="adminUser.mobilePhone"
        label={isPerson ? 'Cellulare' : 'Cellulare del firmatario'}
      />

      {showResponsibleRui ? (
        <div className="grid gap-2">
          <Banner tone="info" title="RUI personale del responsabile">
            Obbligatorio per agenzie/broker società: serve a verificare il
            responsabile nel RUI pubblico IVASS. La sezione è derivata dal tipo.
          </Banner>
          <div className="grid gap-5 sm:grid-cols-2">
            {/* Numero RUI responsabile — sezione (A/B/E) prefisso read-only
                DENTRO la cella; l'utente digita solo le 9 cifre. */}
            <Controller
              control={control}
              name="adminUser.ruiNumber"
              render={({ field }) => (
                <FloatingLabelInput
                  label="Numero RUI del responsabile"
                  prefix={respRuiSection ?? undefined}
                  inputMode="numeric"
                  autoComplete="off"
                  maxLength={9}
                  value={field.value ?? ''}
                  error={respRuiTouched ? errors?.ruiNumber?.message : undefined}
                  onChange={(e) => {
                    field.onChange(normalizeRuiDigits(e.target.value));
                    if (respRuiTouched) setRespRuiTouched(false);
                  }}
                  onBlur={() => {
                    field.onBlur();
                    setRespRuiTouched(true);
                    void handleRespRuiBlur();
                  }}
                />
              )}
            />
            {/* Data iscrizione recuperata dal Registro IVASS (read-only),
                non più digitata (PR-ONBOARDING-RUI-AUTO-LOOKUP-1). */}
            <FloatingLabelInput
              label="Iscrizione RUI"
              labelPosition="top"
              readOnly
              value={formatDateIT(respRuiDate)}
            />
          </div>
          {respRuiTone && respRuiMessage ? (
            <Banner tone={respRuiTone}>{respRuiMessage}</Banner>
          ) : null}
        </div>
      ) : null}

      {/* Turnstile widget — required by the BE `identity-precheck` endpoint
          (anti-bot gate identico al submit-time). Renderizzato qui, non
          nascosto: la regola del progetto è "Turnstile sempre visibile per
          conformità GDPR / consenso challenge". Token monouso → nuovo
          widget ad ogni tentativo, gestito via `precheckTurnstileNonce`. */}
      <div className="grid gap-2">
        <Turnstile
          key={precheckTurnstileNonce}
          siteKey={env.VITE_TURNSTILE_SITE_KEY}
          onSuccess={(token) => setPrecheckTurnstileToken(token)}
          onError={() => setPrecheckTurnstileToken('')}
          onExpire={() => setPrecheckTurnstileToken('')}
          options={{ theme: 'light' }}
        />
      </div>

      {precheckState === 'conflict' ? (
        <Banner
          tone="warning"
          title="Una richiesta con questi dati risulta già a sistema"
        >
          Se hai già avviato un onboarding o pensi sia un errore, contattaci.
          Modifica un identificatore (codice fiscale, P.IVA o email) per
          procedere con dati diversi.
        </Banner>
      ) : null}

      {precheckState === 'error' && precheckErrorMessage ? (
        <Banner tone="info">{precheckErrorMessage}</Banner>
      ) : null}
    </FocusedPageShell>
  );
}
